import { useMemo, useRef } from "react";
import classNames from "classnames";
import { mergeRefs } from "@design-system/overlay/mergeRefs";
import { TextAreaControl } from "./TextAreaParts";
import { TextAreaResizeGrip } from "./TextAreaResizeGrip";
import styles from "./TextArea.module.scss";
import { getTextAreaRootClassName } from "./textAreaRecipe";
import type { TextAreaProps } from "./TextArea.types";
import { useTextAreaAutoGrow } from "./useTextAreaAutoGrow";
import { useTextAreaResize } from "./useTextAreaResize";

/** Design-system multiline text control with optional height behavior. */
export const TextArea = ({
    variant = "primary",
    size,
    invalid = false,
    autoGrow = false,
    minRows,
    maxRows,
    resizable = false,
    className,
    style,
    "data-testid": dataTestid,
    "data-forced-state": forcedState,
    ref,
    rows,
    value,
    onChange,
    disabled,
    readOnly,
    ...props
}: TextAreaProps) => {
    const resolvedSize = size ?? "md";
    const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
    const mergedRef = useMemo(
        () => mergeRefs([ref, textAreaRef]),
        [ref, textAreaRef],
    );
    const resolvedMinRows = minRows ?? rows ?? 2;
    const { style: autoGrowStyle } = useTextAreaAutoGrow({
        ref: textAreaRef,
        isEnabled: autoGrow,
        minRows: resolvedMinRows,
        maxRows,
        value,
    });
    const isGripShown = resizable && !disabled && !readOnly;
    const { gripProps } = useTextAreaResize({
        ref: textAreaRef,
        isEnabled: isGripShown,
    });

    return (
        <div
            className={getTextAreaRootClassName({
                variant,
                size: resolvedSize,
                className: classNames(
                    autoGrow && styles.autoGrow,
                    resizable && styles.resizable,
                    className,
                ),
            })}
            style={style}
            data-variant={variant}
            data-size={resolvedSize}
            data-disabled={disabled ? "" : undefined}
            data-forced-state={forcedState}
            data-auto-grow={autoGrow ? "" : undefined}
            data-resizable={resizable ? "" : undefined}
        >
            <TextAreaControl
                ref={mergedRef}
                aria-invalid={invalid || undefined}
                rows={rows ?? minRows}
                style={autoGrowStyle}
                value={value}
                onChange={onChange}
                disabled={disabled}
                readOnly={readOnly}
                data-testid={dataTestid}
                {...props}
            />
            {isGripShown && <TextAreaResizeGrip {...gripProps} />}
        </div>
    );
};
